/// <amd-module name="Wit/BoardsHub/KanbanBoard/Components/ClickEditField" />
import * as React from "react";
import { useEffect } from "react";
import { IObservableValue, useObservable } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";
import { TextField } from "VSSUI/TextField";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { useBoardEditPermission } from "Wit/BoardsHub/KanbanBoard/Utils/BoardEditPermissionContext";

export interface IClickEditFieldProps {
    inactiveClassName?: string;
    value: string;
    onCommit: (newValue: string) => void;
    disabled?: boolean;
    error?: IObservableValue<string | undefined>;
}

export function ClickEditField({ error, ...props }: IClickEditFieldProps) {
    const [editing, setEditing] = useObservable(false);
    const canEdit = useBoardEditPermission();
    return (
        <Observer error={error} editing={editing}>
            {() => (
                <ClickEditFieldDesign
                    {...props}
                    disabled={props.disabled || !canEdit}
                    error={error?.value}
                    editing={editing.value}
                    onSetEditing={value => setEditing(value)}
                />
            )}
        </Observer>
    );
}

interface IClickEditFieldDesignProps extends Omit<IClickEditFieldProps, "error"> {
    error: string | undefined;
    editing: boolean;
    onSetEditing: (editing: boolean) => void;
}

function ClickEditFieldDesign({ inactiveClassName, value, onCommit, disabled, error, editing, onSetEditing }: IClickEditFieldDesignProps) {
    if (disabled) {
        return <DisabledClickEditField className={inactiveClassName} value={value} />;
    }

    if (!editing && !error) {
        return <InactiveClickEditField className={inactiveClassName} value={value} onActivate={() => onSetEditing(true)} />;
    }

    return <ActiveClickEditField value={value} error={error} onCommit={onCommit} onDeactivate={() => onSetEditing(false)} />;
}

interface IDisabledClickEditFieldProps {
    className?: string;
    value: string;
}

function DisabledClickEditField({ className, value }: IDisabledClickEditFieldProps) {
    return (
        <Tooltip overflowOnly>
            <span className={css("click-edit-field disabled", className)}>{value}</span>
        </Tooltip>
    );
}

interface IInactiveClickEditFieldProps {
    className?: string;
    value: string;
    onActivate: () => void;
}

function InactiveClickEditField({ className, value, onActivate }: IInactiveClickEditFieldProps) {
    const activate = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        onActivate();
    };

    return (
        <Tooltip overflowOnly>
            <span className={css("click-edit-field inactive", className)} tabIndex={0} onClick={activate} onFocus={activate}>
                {value}
            </span>
        </Tooltip>
    );
}

interface IActiveClickEditFieldProps extends Omit<IClickEditFieldDesignProps, "inactiveClassName" | "editing" | "onSetEditing"> {
    onDeactivate: () => void;
}

function ActiveClickEditField({ value, onCommit, error, onDeactivate }: IActiveClickEditFieldProps) {
    const [internalValue, setInternalValue] = useObservable(value);

    // Update our internal copy of the value whenever the prop changes
    useEffect(() => {
        if (value !== internalValue.value) {
            setInternalValue(value);
        }
    }, [value]);

    return (
        <Tooltip text={error} delayMs={0} showOnFocus>
            <TextField
                className={css("click-edit-field active", error && "bolt-textfield-error")}
                value={internalValue}
                autoFocus
                onChange={(e, val) => setInternalValue(val)}
                onBlur={() => {
                    if (value !== internalValue.value) {
                        onCommit(internalValue.value);
                    }
                    onDeactivate();
                }}
                onKeyDown={e => {
                    if (e.key === "Enter") {
                        if (value !== internalValue.value) {
                            onCommit(internalValue.value);
                        }
                        onDeactivate();
                    }
                }}
            />
        </Tooltip>
    );
}
